import React, { useEffect, useState } from 'react'
import { FaPlus, FaEdit, FaEye } from 'react-icons/fa'
import { RiDeleteBin6Line } from 'react-icons/ri'
import { Table, Form, message, Input, Select, Tooltip } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { CustomSelect, Inputs, DeleteModal, CustomModal, SearchOrganizations } from '../../Components'
import { GET, POST } from '../../utils/apis'
import { setFilterUserData, setTotalFilteredUsers, setUserData } from '../../Redux/actions/userAction'
import { secureApi } from '../../Config/api'
import { fetchAllOrganization, fetchUsers, verifyToken } from '../../utils/helpers'
import { DatabaseZap, Search, Building2 } from 'lucide-react'
import { translations, pageSizeOptions } from '../../utils/constants'
import { setUserFilter, setUserPageNumber } from '../../Redux/actions/filterUsersAction'
import { setOrganizationFilter } from '../../Redux/actions/organizationActions'

const initials = (name) => String(name || '').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '-'

const Users = ({ language }) => {
  const usersFromRedux = useSelector((state) => state?.userReducer?.users)
  const filterUsersFromRedux = useSelector((state) => state?.userReducer?.filterUsers)
  const organizationFromRedux = useSelector((state) => state.organizationReducer?.allOrganizations)
  // console.log('organizationFromRedux', organizationFromRedux)
  const authUser = useSelector(state => state?.authReducer?.user)
  const totalFilteredUsers = useSelector(state => state.userReducer?.totalFilteredUsers);
  const user = useSelector(state => state?.authReducer?.user)
  const organizationDetails = useSelector(state => state?.authReducer?.organization)
  const userFilterRedux = useSelector(state => state?.filterUsersReducer?.userFilter)
  const organizationFilter = useSelector(state => state.organizationReducer.organizationFilter)
  const currentPage = useSelector(state => state.filterUsersReducer?.pageNumber || 1);
  const organizationCount = useSelector(state => state.organizationReducer?.orgSelectionCount || 0)
  const history = useHistory()
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const [editUserForm] = Form.useForm()
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [addUserLoading, setAddUserLoading] = useState(false)
  const [viewModal, setViewModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [editModal, setEditModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [userToDelete, setUserToDelete] = useState(null)
  const [userToEdit, setUserToEdit] = useState(null)
  const [editDataLoading, setEditDataLoading] = useState(false)
  const [displayedUsers, setDisplayedUsers] = useState([])
  const [organizations, setOrganizations] = useState([])
  const [organizationId, setOrganizationId] = useState(organizationDetails?.id)
  const rowClassName = (record) => (getRowDisabled(record) ? 'disabled-row' : '')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedOrganizations, setSelectedOrganizations] = useState([])
  const [searchModal, setSearchModal] = useState(false)
  const [initialLoading, setInitialLoading] = useState(false)
  const [pageSize, setPageSize] = useState(10)
  const [totalItems, setTotalItems] = useState(0)

  useEffect(() => {
    if (filterUsersFromRedux) {
      const updatedUsers = filterUsersFromRedux.map(user => ({
        ...user,
        displayRole: roleMapping[user.role?.toLowerCase()] || user.role
      }))
      setDisplayedUsers(updatedUsers)
    }
  }, [filterUsersFromRedux])


  useEffect(() => {
    verifyToken(history, dispatch)
    fetchUsers({ setLoading, setUsers, dispatch, setUserData, authUser, organizationId })
    fetchAllOrganization({ dispatch })
    setOrganizations(organizationFromRedux)
  }, [])

  useEffect(() => {
    if (filterUsersFromRedux?.length === 0) {
      setInitialLoading(true)
      const timeout = setTimeout(() => {
        setInitialLoading(false)
      }, 200)
      return () => clearTimeout(timeout)
    } else {
      setInitialLoading(false)
    }
  }, [filterUsersFromRedux])

  useEffect(() => {
    fetchFilteredUsers()
  }, [currentPage, pageSize, userFilterRedux, searchQuery])

  const fetchFilteredUsers = async () => {
    setLoading(true)
    try {
      const params = {
        user_id: user?.id,
        organization_id: userFilterRedux,
        resourceType: 'users',
        searchQuery,
        page: currentPage,
        limit: pageSize,
      }

      const response = await secureApi?.get(GET?.GET_ORGANIZATION_FILTER, { params })
      if (response?.success) {
        dispatch(setFilterUserData(response?.data))
        dispatch(setTotalFilteredUsers(response?.total || 0));
        setTotalItems(response?.total || 0)
      } else {
        message.error(response?.message || 'Failed to fetch user.')
      }
    } catch (error) {
      message.error('Error fetching filtered users.')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (query) => {
    setSearchQuery(query)
  }

  const handleOrganizationChange = (value) => {
    dispatch(setUserFilter(value))
    dispatch(setOrganizationFilter(value))
  }

  const showModal = async () => {
    const isTokenValid = await verifyToken(history, dispatch)

    if (!isTokenValid) {
      message.error('Session expired. Please log in again.')
      return
    }
    setIsModalVisible(true)
    if (organizationDetails?.id) {
      form.setFieldsValue({
        organization: organizationDetails.id
      });
    }
  }

  const showSearchModal = () => {
    // Refresh the per-organization totals shown in the panel on open.
    if (!searchModal) fetchAllOrganization({ dispatch })
    setSearchModal(!searchModal)
  }

  const viewDetails = (record) => {
    setSelectedUser(record)
    setViewModal(true)
  }

  const openEditModal = (selectedUser) => {
    setViewModal(false)
    setEditModal(true)
  }

  const closeDetails = () => {
    setViewModal(false)
    setSelectedUser(null)
  }

  const closeEditModal = () => {
    editUserForm.resetFields()
    setEditModal(false)
  }

  const handleClose = () => {
    form.resetFields()
    setIsModalVisible(false)
  }

  const handleAction = async () => {
    const isTokenValid = await verifyToken(history, dispatch);
    if (!isTokenValid) {
      message.error('Session expired. Please log in again.');
      return;
    }
    setAddUserLoading(true)
    const values = form.getFieldsValue()
    // console.log(values, 'values')
    if (!values?.name || !values?.email || !values?.role) {
      message.error({
        content: translations[language]?.allFieldsRequired,
      })
      setAddUserLoading(false)
      return
    }

    if (!values?.organization) {
      message.error({
        content: translations[language]?.organizationRequired,
      })
      setAddUserLoading(false)
      return
    }
    try {
      const response = await secureApi.post(POST?.ADD_USER, {
        userName: values?.name,
        email: values?.email,
        password: values?.password,
        phoneNumber: values?.phone,
        role: values?.role,
        organizationId: values?.organization,
        adminId: user?.id,
      })

      if (response.success === true) {
        message.success(translations[language]?.userAddSuccess)
        fetchFilteredUsers()
        fetchUsers({ setLoading, setUsers, dispatch, setUserData, authUser, organizationId })
        form.resetFields()
        setIsModalVisible(false)
      }
      else if (response.success === false) {
        message.error(response?.error || response?.message)
      }
    } catch (error) {
      if (error.response?.message) {
        message.error(error.response.message)
      } else {
        message.error({
          content: 'Failed to add user. Please try again.',
          style: { zIndex: 1050 }
        })
      }
    }
    finally {
      setAddUserLoading(false)
    }
  }

  const handleUpdateUser = async () => {
    const isTokenValid = await verifyToken(history, dispatch)

    if (!isTokenValid) {
      message.error('Session expired. Please log in again.')
      return
    }

    setEditDataLoading(true)
    const values = editUserForm.getFieldsValue()
    // console.log(values, 'values')
    if (!values?.name || !values?.email || !values?.role) {
      message.error('All fields are required.')
      setEditDataLoading(false)
      return
    }

    try {
      const response = await secureApi.post(POST?.UPDATE_USER, {
        userId: userToEdit?.id,
        userName: values?.name,
        email: values?.email,
        phoneNumber: values?.phone,
        role: values?.role,
        organizationId: values?.organization,
      })
      if (response?.success === true) {
        message.success(translations[language]?.userUpdated)
        fetchFilteredUsers()
        fetchUsers({ setLoading, setUsers, dispatch, setUserData, authUser, organizationId })
        editUserForm.resetFields()
        setEditModal(false)
      }
      else if (response?.success === false) {
        message.error(response?.error || response?.message)
      }
    }
    catch (error) {
      if (error.response && error.response.data) {
        message.error(error.response.data.message || 'Failed to update user. Please try again.')
      } else {
        message.error('Network error or something went wrong.')
      }
    }
    finally {
      setEditDataLoading(false)
    }
  }

  const handleUpdate = async (record) => {
    const isTokenValid = await verifyToken(history, dispatch)

    if (!isTokenValid) {
      message.error('Session expired. Please log in again.')
      return
    }

    const id = record?.id
    const name = record?.user_name
    const email = record?.email
    const phoneNumber = record?.phone_number
    const updatedRole = record.displayRole?.toLowerCase()
    const organizationId = record?.organization_id

    if (!updatedRole) {
      message.error(translations[language]?.selectRoleToUpdate)
      return
    }

    try {
      const response = await secureApi.post(POST?.UPDATE_USER, {
        userId: id,
        userName: name,
        email: email,
        phoneNumber: phoneNumber,
        role: updatedRole,
        organizationId: organizationId
      })
      if (response?.success === true) {
        message.success(translations[language]?.userUpdated)
        fetchUsers({ setLoading, setUsers, dispatch, setUserData, authUser, organizationId })
      } else if (response?.success === false) {
        message.error(response.message)
      }
    } catch (error) {
      message.error('Failed to update user role. Please try again.')
    }
  }

  const handleEdit = async (record) => {
    // console.log(record, 'record')
    const isTokenValid = await verifyToken(history, dispatch)
    if (!isTokenValid) {
      message.error('Session expired. Please log in again.')
      return
    }

    setEditModal(true)
    setViewModal(false)
    setUserToEdit(record)


    editUserForm.setFieldsValue({
      name: record?.user_name,
      email: record?.email,
      phone: record?.phone_number,
      role: record?.role,
      organization: record?.organization_id
    })
  }

  const handleDelete = (record) => {
    setDeleteModal(true)
    setUserToDelete(record)
  }

  const getRowDisabled = (record) => {
    const userRole = record?.role?.toLowerCase()
    const authUserRole = authUser?.role?.toLowerCase()
    const isCurrentUser = authUser?.id === record?.id

    if (isCurrentUser) {
      return true
    }
    if (authUserRole === 'superadmin') {
      return false
    }
    if (authUserRole === 'admin') {
      return userRole === 'super Admin'
    }
    if (authUserRole === 'user') {
      return userRole !== 'user'
    }
    return true
  }


  const handleCancel = () => {
    setDeleteModal(false)
    setUserToDelete(null)
  }

  const handleConfirmDelete = async () => {
    const isTokenValid = await verifyToken(history, dispatch)

    if (!isTokenValid) {
      message.error('Session expired. Please log in again.')
      return
    }

    setDeleteModal(false)
    setLoading(true)

    if (userToDelete) {
      try {
        const response = await secureApi.post(POST?.DELETE_USER, {
          userId: userToDelete.id,
        })
        if (response?.success === true) {
          fetchFilteredUsers()
          message.success(`${userToDelete?.user_name} ${translations[language]?.deletedSuccess}`)
          fetchUsers({ setLoading, setUsers, dispatch, setUserData, authUser, organizationId })
        }
        else if (response?.success === false) {
          message.error(response?.error || response?.message)
        }
      } catch (error) {
        message.error('Failed to delete user. Please try again.')
      }

      setLoading(false)
    }

    setUserToDelete(null)
  }

  const handleRoleChange = async (value, record) => {
    const isTokenValid = await verifyToken(history, dispatch)

    if (!isTokenValid) {
      message.error('Session expired. Please log in again.')
      return
    }

    const updatedRecord = { ...record, displayRole: value, role: value.toLowerCase() }

    const updatedUsers = displayedUsers.map(user =>
      user.id === record.id ? updatedRecord : user
    )

    setDisplayedUsers(updatedUsers)
    dispatch(setFilterUserData(updatedUsers))

    try {
      const response = await secureApi.post(POST?.UPDATE_USER, {
        userId: updatedRecord.id,
        userName: updatedRecord.user_name,
        email: updatedRecord.email,
        phoneNumber: updatedRecord.phone_number,
        role: updatedRecord.role,
        organizationId: updatedRecord.organization_id
      })

      if (response?.success) {
        message.success(translations[language]?.userUpdated)
        fetchUsers({ setLoading, setUsers, dispatch, setUserData, authUser, organizationId })
      } else {
        message.error(response?.message || 'Update failed')
      }
    } catch (error) {
      message.error('Failed to update user role. Please try again.')
    }
  }

  const roleMapping = {
    'user': 'User',
    'admin': 'Admin',
    'superAdmin': 'Super Admin',
  }

  const columns = [
    {
      title: translations[language]?.userName,
      dataIndex: 'user_name',
      key: 'user_name',
      render: (text, record) => {
        const isDisabled = getRowDisabled(record)
        const capitalizedText = text.charAt(0).toUpperCase() + text.slice(1)
        return (
          <div
            className='vl-name-cell'
            onClick={!isDisabled ? () => viewDetails(record) : undefined}
            style={isDisabled ? { cursor: 'not-allowed', opacity: 0.5, pointerEvents: 'none' } : undefined}
          >
            <span className='vl-avatar'>{initials(text)}</span>
            <span className='vl-name-text capitalized-name'>{capitalizedText}</span>
            <FaEye className='vl-name-view' />
          </div>
        )
      }
    },
    {
      title: translations[language]?.enterEmailLabel,
      dataIndex: 'email',
      key: 'email',
      render: (text, record) => {
        const isDisabled = getRowDisabled(record);
        return (
          <div
            onClick={!isDisabled ? () => viewDetails(record) : undefined}
            style={{
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              opacity: isDisabled ? 0.5 : 1,
              pointerEvents: isDisabled ? 'none' : 'auto'
            }}
          >
            {text ? text : <span className='vl-muted'>-</span>}
          </div>
        );
      }
    },
    // Super admins see every organisation's users in one list, so each row
    // says which organisation the user belongs to. Admins only ever see
    // their own organisation, where the column would be noise.
    ...(authUser?.role === 'superAdmin' ? [{
      title: translations[language]?.organizationLabel,
      dataIndex: 'organization_name',
      key: 'organization_name',
      render: (text, record) => {
        const isDisabled = getRowDisabled(record)
        if (!text) return <span className='vl-muted'>-</span>
        return (
          <div className='vl-org-cell' style={isDisabled ? { opacity: 0.5 } : undefined}>
            {record?.organization_logo
              ? <img src={record.organization_logo} alt='' className='vl-org-logo' />
              : <span className='vl-avatar vl-org-avatar'>{initials(text)}</span>}
            <span className='vl-org-name'>{text}</span>
          </div>
        )
      }
    }] : []),
    {
      title: translations[language]?.roleSelect,
      dataIndex: 'role',
      key: 'role',
      render: (text, record) => {
        const isDisabled = getRowDisabled(record)
        const currentRole = record?.displayRole || roleMapping[text]

        return (
          <Select
            value={currentRole}
            key={`role-select-${record.id}-${currentRole}`}
            onChange={value => handleRoleChange(value, record)}
            style={{
              width: 120,
              opacity: isDisabled ? 0.5 : 1
            }}
            disabled={isDisabled}
          >
            <Select.Option value="Admin">{translations[language]?.admin}</Select.Option>
            <Select.Option value="User">{translations[language]?.userText}</Select.Option>
          </Select>
        )
      }
    },
    {
      title: translations[language]?.action,
      key: 'action',
      render: (_, record) => {
        const isDisabled = getRowDisabled(record)

        return (
          <div
            className='vl-actions'
            style={{
              opacity: isDisabled ? 0.5 : 1,
              pointerEvents: isDisabled ? 'none' : 'auto'
            }}
          >
            <Tooltip title={translations[language]?.edit}>
              <button
                type='button'
                className='vl-action-btn vl-action-edit'
                disabled={isDisabled}
                onClick={!isDisabled ? () => handleEdit(record) : undefined}
              >
                <FaEdit size={14} />
              </button>
            </Tooltip>
            <Tooltip title={translations[language]?.delete}>
              <button
                type='button'
                className='vl-action-btn vl-action-delete'
                disabled={isDisabled}
                onClick={!isDisabled ? () => handleDelete(record) : undefined}
              >
                <RiDeleteBin6Line size={15} />
              </button>
            </Tooltip>
          </div>
        )
      }
    }
  ]


  return (
    <div className='main-container'>
      <div className='main-inner-container'>
        <h1 className='main-heading'>{translations[language]?.usersHeading}</h1>
        <div className='insider-container'>
          {usersFromRedux?.length === 0
            ?
            null
            : (
              <div className='insider-container-buttons'>
                <div>
                  <buttons className='add-button' onClick={showModal}
                  >
                    <FaPlus className='icon' />
                    {translations[language]?.addUserButton}
                  </buttons>
                </div>

                <div className='search-main-div'>
                  <div className="search-inner-div">
                    <Search className="search-icon" />
                    <input
                      placeholder={translations[language]?.searchUsers}
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="search-input"
                    />
                    {searchQuery && (
                      <span className="clear-icon" style={{ cursor: "pointer" }} onClick={() => handleSearch("")}>
                        ✕
                      </span>
                    )}
                  </div>
                  {authUser?.role === 'superAdmin' ? <div className='get-reports-container'>
                    <span type='primary'
                      className={`organization ${searchModal ? 'open-organization' : 'close-organization'}`}
                      onClick={showSearchModal}
                    >
                      <Building2 />
                      {translations[language]?.organizations}
                      {organizationCount ? <div className="filter-count-badge">
                        {organizationCount}
                      </div> : null}
                    </span>
                  </div> :
                    null
                  }
                </div>

              </div>
            )}
          <div className='vd-card'>
            {usersFromRedux?.length === 0 ?
              <div className='vd-card-body'>
                <div className="no-vehicles-container">
                  <div className="no-vehicles-text-center">
                    <div className="flex-col-center">
                      <div className="no-vehicles-text-center">
                        <DatabaseZap className='no-vehicle-icon' />
                        <h3 className="no-vehicles-heading">{translations[language]?.userNotFound}</h3>
                        <p className="no-vehicles-description">
                          {translations[language]?.adduserToSeeItsList}
                        </p>
                        <div>
                          <buttons className='add-button' onClick={showModal}
                          >
                            <FaPlus className='icon' />
                            {translations[language]?.addUserButton}
                          </buttons>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              :
              (
                <>
                  <div className='vd-card-header'>
                    <div className='vl-list-title'>
                      <h2 className='vd-card-title'>{translations?.[language]?.userNameList}</h2>
                      <span className='vl-total-chip'>{translations[language]?.totalRecord}: <strong>{totalFilteredUsers}</strong></span>
                    </div>
                  </div>
                  <div className='vd-card-body'>
                    <Table
                      className='vl-table'
                      dataSource={filterUsersFromRedux}
                      columns={columns}
                      pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalItems,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        pageSizeOptions,
                        onChange: (page, pageSize) => {
                          dispatch(setUserPageNumber(page))
                          setPageSize(pageSize)
                        },
                        onShowSizeChange: (current, size) => {
                          setPageSize(size)
                          dispatch(setUserPageNumber(1))
                        }
                      }}
                      scroll={{ x: 300 }}
                      loading={initialLoading}
                    />
                  </div>
                </>
              )}
          </div>
        </div>
      </div>

      <CustomModal
        isVisible={isModalVisible}
        onClose={handleClose}
        title={translations[language]?.addUserButton}
        actionText={translations[language]?.addUserButton}
        onAction={handleAction}
        loading={addUserLoading}
        language={language}
      >
        <Form
          form={form}
          layout='vertical'
        >
          <Inputs
            label={translations[language]?.enterUserName}
            placeholder={translations[language]?.enterUserName}
            name='name'
            rules={[{ required: true, message: translations[language]?.pleaseEnterName }]}
          />
          <div className='flex-input'>
            <div
              className='flex-inner-inputs'
            >
              <Inputs
                className='flex-inner-inputs'
                label={translations[language]?.enterEmailLabel}
                placeholder={translations[language]?.enterEmail}
                name='email'
                type='email'
                rules={[{ required: true, message: translations[language]?.pleaseEnterEmail }]}
              />
            </div>
            <div
              style={{ marginLeft: 5 }}
              className='flex-inner-inputs'>
              <Form.Item
                name='password'
                label={translations[language]?.paswordtxt}
                rules={[{ required: true, message: translations[language]?.pleaseEnterPasword }]}
              >
                <Input.Password placeholder={translations[language]?.enterPassword} className='custom-inputs' />
              </Form.Item>

            </div>

          </div>

          <div className='flex-input'>
            <div className='flex-inner-inputs' style={{ marginRight: 5 }}>
              <Inputs
                label={translations[language]?.phoneNumber}
                placeholder={translations[language]?.enterPhoneNumber}
                name='phone'
                type='tel'
                onInput={(e) => {
                  e.target.value = e.target.value.replace(/[^0-9]/g, '')
                }}
              />
            </div>

            <div className='flex-inner-inputs'>
              <CustomSelect
                label={translations[language]?.roleSelect}
                placeholder={translations[language]?.roleSelectType}
                name='role'
                options={[
                  { label: translations[language]?.admin, value: 'admin' },
                  { label: translations[language]?.userText, value: 'user' },
                ]}
                rules={[{ required: true, message: translations[language]?.selectRoleMessage }]}
              />
            </div>

          </div>

          <div>
            {/* <CustomSelect
              label={translations[language]?.organizationSelect}
              placeholder={translations[language]?.organizationSelect}
              name="organization"
              options={organizations.map(org => ({
                label: org.organization_name,
                value: org.id,
              }))}
              rules={[{ required: true, message: translations[language]?.organizationSelect }]}
            /> */}

            <CustomSelect
              label={translations[language]?.organizations}
              placeholder={translations[language]?.organizationSelect}
              name="organization"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
              options={
                organizationDetails?.id
                  ? [{
                    label: organizationDetails?.name,
                    value: organizationDetails?.id
                  }]
                  : organizations?.map(org => ({
                    label: org?.organization_name,
                    value: org?.id
                  }))
              }
              value={organizationDetails?.id}
              defaultValue={organizationDetails?.name}
              disabled={!!organizationDetails?.id}
              rules={[{
                required: true,
                message: translations[language]?.organizationSelect
              }]}
            />
          </div>

        </Form>
      </CustomModal>

      <CustomModal
        isVisible={viewModal}
        onClose={closeDetails}
        title={translations[language]?.userDetailsHeading}
        actionText={translations[language]?.editUserText}
        onAction={() => handleEdit(selectedUser)}
        language={language}
      >
        <div className='device-information-section'>
          <div className='inner-information-div'>
            <div className='info-childern-div'>
              <div className='inner-information-childern'>
                <h3 className='info-heading'>{translations[language]?.userName}</h3>
                <p>{selectedUser?.user_name}</p>
              </div>
            </div>
            <div className='info-childern-div'>
              <div className='inner-information-childern'>
                <h3 className='info-heading'>{translations[language]?.enterEmailLabel}</h3>
                <p>{selectedUser?.email}</p>
              </div>
            </div>
          </div>

          <div className='inner-information-div'>
            {/* <div className='info-childern-div'>
                <div className='inner-information-childern'>
                  <h3 className='info-heading'>{translations[language]?.paswordtxt}</h3>
                  <p className='see-change-password' onClick={() => history.push({
                    pathname: paths?.SETTINGS,
                    state: { activeTab: '2' }
                  })}>
                    <FaEye />
                    {translations[language]?.seePassword}
                  </p>
                </div>
              </div> */}
            <div className='info-childern-div'>
              <div className='inner-information-childern'>
                <h3 className='info-heading'>{translations[language]?.phoneNumber}</h3>
                <p>{selectedUser?.phone_number || '-'}</p>
              </div>
            </div>
            <div className='info-childern-div'>
              <div className='inner-information-childern'>
                <h3 className='info-heading'>{translations[language]?.accessAllow}</h3>
                <p>{new Date().getFullYear()}</p>
              </div>
            </div>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        isVisible={editModal}
        onClose={closeEditModal}
        actionText={translations[language]?.updateUserButton}
        title={translations[language]?.editUserText}
        onAction={handleUpdateUser}
        loading={editDataLoading}
        language={language}
      >
        <Form
          form={editUserForm}
          layout='vertical'
        >
          <Inputs
            label={translations[language]?.enterUserName}
            placeholder={translations[language]?.enterUserName}
            name='name'
            rules={[{ required: true, message: translations[language]?.pleaseEnterName }]}
          />
          <div className='flex-input'>
            <div
              className='flex-inner-inputs'
            >
              <Inputs
                className='flex-inner-inputs'
                label={translations[language]?.enterEmailLabel}
                placeholder={translations[language]?.enterEmail}
                name='email'
                type='email'
                disabled
                rules={[{ required: true, message: translations[language]?.pleaseEnterEmail }]}
              />
            </div>
            <div className='flex-inner-inputs' style={{ marginLeft: 5 }}>
              <Inputs
                label={translations[language]?.phoneNumber}
                placeholder={translations[language]?.enterPhoneNumber}
                name='phone'
                type='tel'
              />
            </div>
          </div>

          <div className='flex-input'>
            <div className='flex-inner-inputs'>
              <CustomSelect
                label={translations[language]?.roleSelect}
                name="role"
                options={[
                  { label: translations[language]?.admin, value: 'admin' },
                  { label: translations[language]?.userText, value: 'user' },
                ]}
                rules={[{ required: true, message: 'Please select a role!' }]}
              />
            </div>
            <div className='flex-inner-inputs' style={{ marginLeft: 5 }}>
              {/* <CustomSelect
                label={translations[language]?.organizationSelect}
                placeholder={translations[language]?.organizationSelect}
                name="organization"
                options={organizations.map(org => ({
                  label: org.organization_name,
                  value: org.id,
                }))}
                rules={[{ required: true, message: translations[language]?.organizationSelect }]}
              /> */}

              <CustomSelect
                label={translations[language]?.organizations}
                placeholder={translations[language]?.organizationSelect}
                name="organization"
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                options={
                  organizationDetails?.id
                    ? [{
                      label: organizationDetails?.name,
                      value: organizationDetails?.id
                    }]
                    : organizations?.map(org => ({
                      label: org?.organization_name,
                      value: org?.id
                    }))
                }
                value={editUserForm.getFieldValue("organization")}
                disabled={!!organizationDetails?.id}
                rules={[{ required: true, message: translations[language]?.organizationSelect }]}
              />

            </div>

          </div>

        </Form>
      </CustomModal>

      <DeleteModal
        title={translations[language]?.deleteUser}
        bodyText={
          <>
            {translations[language]?.areYouSureYouWantToDltThisUser}
          </>
        }
        isOpen={deleteModal}
        onCancel={handleCancel}
        onDelete={handleConfirmDelete}
        language={language}
      />
      <div className={`search-organization-main-div ${searchModal ? 'open' : ''}`}>
        <SearchOrganizations
          selectedOrganizations={organizationFilter}
          handleOrganizationChange={handleOrganizationChange}
          organizations={organizations}
          setSearchModal={setSearchModal}
          searchModal={searchModal}
          selectedFilter={userFilterRedux}
          language={language}
          searchType={"users"}
        />
      </div>
    </div>
  )
}

export default Users